import React from 'react';
import { Route, IndexRedirect, IndexRoute } from 'react-router';

import { getChildLogger } from '#/shared/utils/client.logger';

import AppLayout from './containers/AppLayout';
import NotFound from './containers/NotFound';

const log = getChildLogger('routes');

/**
 * @description This should help us begin to make better use of some
 * folder structures that will scale a bit better with our App Size.
 */
function $importRoute(_location, cb, path) {
  return import(/* webpackChunkName: '[route]' */ `../routes/${path}`)
    .then((module) => cb(null, module.default))
    .catch((err) => {
      if (!/document is not defined/i.test(err.message)) {
        log.error('Dynamic "route" loading failed', err);
      }
    });
}

function $import(_location, cb, component) {
  return import(/* webpackChunkName: '[request]' */ `./containers/${component}`)
    .then((module) => cb(null, module.default))
    .catch((err) => {
      if (!/document is not defined/i.test(err.message)) {
        log.error('Dynamic page loading failed', err);
      }
    });
}

/**
 * Roles available in app. See SE-534 for More
 * -------------------------------------------
 * - user: anyone with a login + password
 * - admin: Super user - reserved for internal use
 * - employer: "Admin" of each company - this is the master account
 * - company-agent: "Agent" for each company, restricted permissions
 * - worker: One of our partners - available for work and using the mobile app
 *
 * Permission Aliases:
 * -------------------
 * - "*" : Allows any logged in user to access the route
 * - "guest" : Allows non-authenticated users to access the route
 */

export default (
  <Route className="pl5-ns" component={AppLayout} path="/">
    <IndexRoute
      getComponent={(loc, cb) => $import(loc, cb, 'Dashboard')}
      name="Dashboard"
    />

    <Route
      getComponent={(loc, cb) => $import(loc, cb, 'Auth')}
      path="auth"
      roles={['guest', '*']}
    />

    <Route
      getComponent={(loc, cb) => $import(loc, cb, 'Callback')}
      path="callback"
      roles={['guest', '*']}
    />

    {/** SSM Core : START */}

    <Route name="Shifts" path="shifts" roles={['employer', 'company-agent']}>
      <IndexRoute
        getComponent={(loc, cb) => $import(loc, cb, 'ShiftsNextGen')}
      />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'ShiftUpload')}
        path="upload"
      />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'CreateShift')}
        path="create(/:type)"
      />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'ShiftDetails')}
        path="(:shiftId)"
      />
    </Route>

    <Route
      name="In Shift Tasks"
      path="inshifttasks"
      roles={['employer', 'company-agent']}
    >
      <IndexRoute
        getComponent={(loc, cb) => $import(loc, cb, 'InShiftTasksContainer')}
      />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'InShiftTaskSummaryPage')}
        path="(:taskId)"
      />
    </Route>

    <Route
      name="Task List Review"
      path="task-review"
      roles={['employer', 'company-agent']}
    >
      <IndexRoute
        getComponent={(loc, cb) => $import(loc, cb, 'TaskListReview')}
      />
    </Route>

    <Route
      name="Dispatch Prefs"
      path="dispatchPrefs"
      roles={['employer', 'company-agent']}
    >
      <IndexRoute
        getComponent={(loc, cb) =>
          $import(loc, cb, 'dispatchPrefs/DispatchPrefsList')
        }
      />
      <Route
        getComponent={(loc, cb) =>
          $import(loc, cb, 'dispatchPrefs/DispatchPrefDetails')
        }
        path="(:dispatchPrefId)"
      />
    </Route>

    <Route
      getComponent={(loc, cb) => $import(loc, cb, 'ScheduleExploration')}
      name="ScheduleExploration"
      path="schedules(/:partnerId)"
      roles={['admin']}
    />

    <Route path="partners" roles={['employer', 'company-agent']}>
      <IndexRoute getComponent={(loc, cb) => $import(loc, cb, 'Partners')} />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'TagUpload')}
        path="tagupload"
      />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'PartnerDetails')}
        path="(:userId)"
      />
    </Route>

    <Route path="pools" roles={['employer', 'company-agent']}>
      <IndexRoute getComponent={(loc, cb) => $import(loc, cb, 'Pools')} />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'PoolUpload')}
        path="upload(/:poolId)"
      />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'Pools')}
        path="(:poolId)"
      />
    </Route>

    {/** SSM Core : END */}

    {/** Retail : START */}
    <Route path="retail" roles={['employer', 'company-agent']}>
      <IndexRoute
        getComponent={(loc, cb) => $import(loc, cb, 'campaigns/Campaigns')}
      />

      <Route path="campaigns" roles={['employer', 'company-agent']}>
        <IndexRoute
          getComponent={(loc, cb) => $import(loc, cb, 'campaigns/Campaigns')}
        />

        <Route path="work">
          <IndexRoute
            getComponent={(loc, cb) =>
              $import(loc, cb, 'campaigns/CampaignWork')
            }
          />

          <Route
            getComponent={(loc, cb) =>
              $import(loc, cb, 'campaigns/CampaignWorkReview')
            }
            path=":assignmentId"
          />
        </Route>

        <Route path=":campaignId">
          <IndexRoute
            getComponent={(loc, cb) => $import(loc, cb, 'campaigns/Campaigns')}
          />

          <Route
            getComponent={(loc, cb) =>
              $import(loc, cb, 'campaigns/CampaignQuotaEditor')
            }
            path="quotas"
          />
        </Route>
      </Route>
      <Route path="storeLists" roles={['employer', 'company-agent']}>
        <IndexRoute
          getComponent={(loc, cb) =>
            $import(loc, cb, 'storeLists/StoreListsList')
          }
        />
        <Route
          getComponent={(loc, cb) =>
            $import(loc, cb, 'storeLists/StoreListDetails')
          }
          path="(:storeListId)"
        />
      </Route>

      <Route path="surveys" roles={['employer', 'company-agent']}>
        <IndexRoute getComponent={(loc, cb) => $import(loc, cb, 'Surveys')} />

        <Route
          getComponent={(loc, cb) => $import(loc, cb, 'SurveyBuilder')}
          path="builder"
          roles={['employer', 'company-agent']}
        />

        <Route path="(:surveyId)">
          <IndexRoute getComponent={(loc, cb) => $import(loc, cb, 'Survey')} />
          <Route
            getComponent={(loc, cb) => $import(loc, cb, 'SurveyBuilder')}
            path="edit"
          />
        </Route>
      </Route>

      <Route path="products" roles={['employer', 'company-agent']}>
        <IndexRoute getComponent={(loc, cb) => $import(loc, cb, 'Products')} />
        <Route
          getComponent={(loc, cb) => $import(loc, cb, 'Product')}
          path="(:productId)"
        />
      </Route>
    </Route>
    {/** Retail : END */}

    <Route
      getComponent={(loc, cb) => $import(loc, cb, 'Profile')}
      path="profile"
      roles={['user']}
    />

    <Route path="password" roles={['user']}>
      <IndexRoute
        getComponent={(loc, cb) => $import(loc, cb, 'ChangePassword')}
      />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'ForgotPassword')}
        path="forgot"
        roles={['*', 'guest']}
      />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'PasswordReset')}
        path="reset/:token"
        roles={['*', 'guest']}
      />
    </Route>

    <Route
      getComponent={(loc, cb) => $import(loc, cb, 'ActivateAccount')}
      path="activate(/:companyId)"
      roles={['employer', 'company-agent']}
    />

    <Route path="admin" roles={['admin']}>
      <IndexRoute
        getComponent={(loc, cb) => $import(loc, cb, 'admin/Companies')}
      />

      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'admin/UserReviewList')}
        path="users(/:userId)"
      />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'admin/EmployerUsersList')}
        path="employers(/:userId)"
      />

      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'admin/AdminUsersList')}
        path="administrators(/:userId)"
      />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'admin/AuditLogList')}
        path="audit"
      />
      <Route
        getComponent={(loc, cb) =>
          $import(loc, cb, 'admin/PhoneVerificationList')
        }
        path="phoneVerification"
      />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'AdminContainer')}
        path="certs"
      />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'AdminContainer')}
        path="taggroups"
      />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'AdminContainer')}
        path="roles"
      />
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'AdminContainer')}
        path="positions"
      />

      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'AdminContainer')}
        path="utils"
      />

      <Route path="companies(/:companyId)">
        <IndexRoute
          getComponent={(loc, cb) => $import(loc, cb, 'admin/Companies')}
        />
      </Route>

      <Route
        getComponent={(loc, cb) =>
          $import(loc, cb, 'admin/payments/BulkCreateTransfers')
        }
        path="paymentUpload"
      />

      <Route path="payments">
        <IndexRedirect to="paymentAccounts" />
        <Route
          getComponent={(loc, cb) => $import(loc, cb, 'admin/payments/Config')}
          path="config"
        />
      </Route>
      <Route
        getComponent={(loc, cb) => $import(loc, cb, 'admin/payments/Accounts')}
        path="paymentAccounts(/:accountId)"
      />
      <Route
        getComponent={(loc, cb) =>
          $import(loc, cb, 'admin/payments/Transactions')
        }
        path="transactions"
      />
      <Route
        getComponent={(loc, cb) =>
          $import(loc, cb, 'admin/payments/PaymentProcessingQueue')
        }
        path="paymentProcessingQueue"
      />
    </Route>
    <Route
      getComponent={(loc, cb) => $import(loc, cb, 'admin/Companies')}
      path="company"
    />

    <Route path="locations">
      <IndexRoute getComponent={(loc, cb) => $import(loc, cb, 'Locations')} />

      <Route
        getComponent={(loc, cb) =>
          $importRoute(loc, cb, 'locations/pages/LocationDetails')
        }
        path=":locationId"
      />
    </Route>

    {process.env.NODE_ENV === 'development' && (
      <Route
        getComponent={(loc, cb) =>
          $import(loc, cb, 'statePlayground/StatePlayground')
        }
        path="state-playground"
      />
    )}

    <Route component={NotFound} path="*" roles={['guest', '*']} status={404} />
  </Route>
);
